const KEY_SELECTOR = '[data-template-key]';

const toKebabCase = (property) =>
  property.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);

/**
 * Normalises a CSS value the way the browser serialises it, so `#ff0000`
 * and `rgb(255, 0, 0)` compare equal.
 * @function
 *
 * @param {Document} ownerDocument - The document to create the scratch element in.
 * @param {string} property - The CSS property, in kebab case.
 * @param {string} value - The value to normalise.
 * @returns {string} The serialised value.
 */
const normalizeCssValue = (ownerDocument, property, value) => {
  const scratch = ownerDocument.createElement('span');
  scratch.style.setProperty(property, value);
  return scratch.style.getPropertyValue(property);
};

/**
 * Reads what the commit applies: a tag (strong, em, u, s, sub, sup…) or
 * inline style declarations. Block-level and list commits are left alone, a
 * key is an inline token.
 * @function
 *
 * @param {Object} commitStyle - The CommitStyle passed by `afterCommitStyle`.
 * @returns {Object|null} `{ tag, declarations, toggles }`, or null when nothing applies to a key.
 */
const describeCommit = (commitStyle) => {
  if (!commitStyle || commitStyle.elementIsBlock || commitStyle.elementIsList) {
    return null;
  }
  const declarations = Object.entries(
    commitStyle.options?.attributes?.style || {}
  )
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([property, value]) => [toKebabCase(property), String(value)]);
  const tag = (
    commitStyle.isElementCommit ? commitStyle.element : 'span'
  ).toLowerCase();

  if (tag === 'span' && !declarations.length) return null;

  // Jodit toggles an element (bold twice removes it) but sets a style value
  // (12px twice stays 12px, 16px replaces 12px).
  return { tag, declarations, toggles: tag !== 'span' };
};

const carriesCommit = (element, commit) =>
  element.tagName.toLowerCase() === commit.tag &&
  commit.declarations.every(
    ([property, value]) =>
      element.style.getPropertyValue(property) ===
      normalizeCssValue(element.ownerDocument, property, value)
  );

/**
 * Finds the closest ancestor, up to the editor root, carrying the commit.
 * @function
 *
 * @param {Node} node - The node to start from.
 * @param {Object} commit - The described commit.
 * @param {HTMLElement} root - The editor root.
 * @returns {HTMLElement|null} The carrier, if any.
 */
const findCarrier = (node, commit, root) => {
  let current = node.nodeType === 3 ? node.parentElement : node;
  while (current && current !== root) {
    if (carriesCommit(current, commit)) return current;
    current = current.parentElement;
  }
  return null;
};

// Top-level keys only: a key nested in a condition inherits from the
// condition's own wrapper.
const intersectingKeys = (root, range) =>
  Array.from(root.querySelectorAll(KEY_SELECTOR)).filter(
    (key) =>
      !key.parentElement.closest(KEY_SELECTOR) && range.intersectsNode(key)
  );

/**
 * Finds the first selected text node that is not part of a key: what Jodit
 * did to it tells whether the style was applied or removed.
 * @function
 *
 * @param {HTMLElement} root - The editor root.
 * @param {Range} range - The selection range.
 * @returns {Text|null} The reference text node, if any.
 */
const referenceTextNode = (root, range) => {
  const walker = root.ownerDocument.createTreeWalker(
    range.commonAncestorContainer,
    NodeFilter.SHOW_TEXT
  );
  let node = walker.nextNode();
  while (node) {
    if (
      node.textContent.trim() &&
      range.intersectsNode(node) &&
      !node.parentElement.closest(KEY_SELECTOR)
    ) {
      return node;
    }
    node = walker.nextNode();
  }
  return null;
};

// A wrapper holding only the key and already setting the committed
// properties, whatever their values: 16px must update it, not nest into it.
const ownWrapperWithProperties = (key, commit) => {
  const parent = key.parentElement;
  const isOwnWrapper =
    parent &&
    parent.childNodes.length === 1 &&
    parent.tagName.toLowerCase() === commit.tag;
  const setsProperties =
    isOwnWrapper &&
    commit.declarations.length > 0 &&
    commit.declarations.every(
      ([property]) => parent.style.getPropertyValue(property) !== ''
    );
  return setsProperties ? parent : null;
};

const applyDeclarations = (element, commit) => {
  const { style } = element;
  commit.declarations.forEach(([property, value]) =>
    style.setProperty(property, value)
  );
};

const wrapKey = (key, commit) => {
  const own = ownWrapperWithProperties(key, commit);
  if (own) {
    applyDeclarations(own, commit);
    return;
  }
  const wrapper = key.ownerDocument.createElement(commit.tag);
  applyDeclarations(wrapper, commit);
  key.parentNode.insertBefore(wrapper, key);
  wrapper.appendChild(key);
};

/**
 * Removes the wrappers Jodit created inside an editable key (a condition):
 * once the key itself is wrapped they are redundant, and they break the
 * generator's parsing of `{{Si <key>}}`.
 * @function
 *
 * @param {HTMLElement} key - The key element.
 * @param {Object} commit - The described commit.
 * @returns {boolean} True when a wrapper was removed.
 */
const stripInnerWrappers = (key, commit) => {
  const redundant = Array.from(key.querySelectorAll(commit.tag)).filter(
    (element) =>
      !element.hasAttribute('data-template-key') &&
      !element.closest(
        `${KEY_SELECTOR}:not([data-template-key="${key.getAttribute(
          'data-template-key'
        )}"])`
      ) &&
      carriesCommit(element, commit)
  );
  redundant.forEach((element) => {
    const parent = element.parentNode;
    while (element.firstChild) parent.insertBefore(element.firstChild, element);
    parent.removeChild(element);
  });
  return redundant.length > 0;
};

/**
 * Takes the key out of its carrier: unwraps the carrier when the key is all
 * it holds, otherwise splits the carrier around the key.
 * @function
 *
 * @param {HTMLElement} key - The key element.
 * @param {HTMLElement} carrier - The ancestor carrying the commit.
 * @returns {void}
 */
const unwrapKey = (key, carrier) => {
  const parent = carrier.parentNode;
  if (carrier.childNodes.length === 1 && carrier.firstChild === key) {
    parent.insertBefore(key, carrier);
    parent.removeChild(carrier);
    return;
  }
  const after = carrier.cloneNode(false);
  let node = key.nextSibling;
  while (node) {
    const next = node.nextSibling;
    after.appendChild(node);
    node = next;
  }
  parent.insertBefore(key, carrier.nextSibling);
  if (after.childNodes.length) parent.insertBefore(after, key.nextSibling);
  if (!carrier.childNodes.length) parent.removeChild(carrier);
};

/**
 * Applies to the template keys the style Jodit has just applied to the
 * selection. Jodit skips non-editable elements, so a key inside a selection
 * kept its size, weight or colour while the text around it changed. The key
 * is wrapped in the same element as its neighbours, which is what the
 * generator keeps once it replaces the key by its value.
 *
 * Style values (size, colour) are set or updated. Elements (bold, italic…)
 * follow Jodit's toggle: what happened to the selected text decides, and
 * with only keys selected the first key's own state decides.
 * @function
 *
 * @param {Object} editor - The Jodit editor instance.
 * @param {Object} commitStyle - The CommitStyle passed by `afterCommitStyle`.
 * @returns {boolean} True when the document was changed.
 */
export const syncTemplateKeysStyle = (editor, commitStyle) => {
  const commit = describeCommit(commitStyle);
  const root = editor?.editor;
  const range = editor?.s?.range;
  if (!commit || !root || !range) return false;

  const keys = intersectingKeys(root, range);
  if (!keys.length) return false;

  let applied = true;
  if (commit.toggles) {
    const reference = referenceTextNode(root, range);
    applied = reference
      ? Boolean(findCarrier(reference, commit, root))
      : !findCarrier(keys[0], commit, root);
  }

  let changed = false;
  keys.forEach((key) => {
    const carrier = findCarrier(key, commit, root);
    if (applied && !carrier) {
      wrapKey(key, commit);
      changed = true;
    } else if (!applied && carrier) {
      unwrapKey(key, carrier);
      changed = true;
    }
    if (applied && stripInnerWrappers(key, commit)) changed = true;
  });

  if (changed) editor.synchronizeValues();
  return changed;
};
